import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './firebase';
import { apiClient } from './apiClient';

const PermissionsContext = createContext();

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
    const [permissions, setPermissions] = useState({});
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const currentUser = useAuth();

    const fetchPermissions = useCallback(async () => {
        if (!currentUser) {
            setPermissions({});
            setIsAdmin(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            // Corrected endpoint to fetch the full user profile
            const response = await apiClient.get('/v1/user/me', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch permissions');
            }

            const data = await response.json(); // The full user profile
            
            // Extract permissions and admin status from the user profile object
            setPermissions(data.permissions || {});
            setIsAdmin(data.role === 'admin');

        } catch (error) {
            console.error("Error fetching permissions:", error);
            // In case of error, default to no permissions.
            setPermissions({});
            setIsAdmin(false);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    // This function remains the same, but now relies on correctly fetched state.
    const hasPermission = (permission) => {
        if (isAdmin) {
            return true;
        }
        // This logic is now more complex, so we'll adjust it if needed.
        // For now, we assume a simple structure.
        const keys = permission.split('.');
        let current = permissions;
        for (const key of keys) {
            if (current[key] === undefined) {
                return false;
            }
            current = current[key];
        }
        return !!current;
    };

    const value = {
        permissions,
        isAdmin,
        hasPermission,
        loadingPermissions: loading,
        refetchPermissions: fetchPermissions,
    };

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
};